<template>
  <div class="v-datepicker">
    <!-- Single date -->
    <VPopper
      v-if="!isRange && !Array.isArray(currentValue)"
      ref="popper"
      :click-ignore-on="['.v-icon-close']"
      lazy
      mount-to="#v-poppers"
      :offset="5"
      placement="bottom-start"
      popper-class="v-datepicker__popper"
      show-arrow
      style="width: 100%"
      transition="v-zoom-in-top"
      :trigger="disabled ? 'disabled' : 'focus'"
      @blur="focused = false"
      @hide="save()"
      @show="input?.focus()"
    >
      <template #reference>
        <div ref="inputs" class="v-datepicker__inputs" :class="{ 'is-range': isRange }">
          <VInput
            ref="input"
            :clearable
            :disabled
            :error="errors.length ? ' ' : ''"
            :help
            :label
            :maxlength="maxLength"
            :minimal
            :model-value="currentValue"
            :optional
            :placeholder
            :required="isRequired"
            :size
            :status
            :suggestion
            @blur="save()"
            @clear="clear()"
            @input="onInput"
            @keydown.enter="save()"
            @update:model-value="onInput"
          >
            <template #prefix>
              <VIcon class="v-input__icon" icon="Calendar" type="placeholder" />
            </template>

            <template #below>
              <VErrors :errors />
            </template>
          </VInput>
        </div>
      </template>

      <VCalendar
        :clear-button-text
        :clearable
        :confirm-button-text
        :date-is-disabled
        :max="toDayjs(max)"
        :min="toDayjs(min)"
        :model-value="toDayjs(currentValue)"
        :precision
        :quick-options
        :style="{ width: `${popperWidth}px` }"
        @confirm="onConfirm"
        @update:model-value="currentValue = fromValue($event)"
      />
    </VPopper>

    <!-- Date range -->
    <VPopper
      v-else
      ref="popper"
      :click-ignore-on="['.v-icon-close']"
      lazy
      mount-to="#v-poppers"
      :offset="5"
      placement="bottom-start"
      popper-class="v-datepicker__popper"
      show-arrow
      style="width: 100%"
      transition="v-zoom-in-top"
      :trigger="disabled ? 'disabled' : 'focus'"
      @blur="focused = false"
      @hide="save()"
      @show="input?.focus()"
    >
      <template #reference>
        <div ref="inputs" class="v-datepicker__inputs" :class="{ 'is-range': isRange, 'is-focused': focused, 'is-hovering': input?.hovering || inputEnd?.hovering }">
          <VInput
            ref="input"
            :class="{ focused, 'has-label': hasLabel }"
            :disabled
            :error="errors.length ? ' ' : ''"
            :label="startLabel"
            :maxlength="maxLength"
            :minimal
            :model-value="currentValue[0]"
            :placeholder="startPlaceholder"
            :size
            :status
            :suggestion="currentValue[0] ? undefined : suggestion"
            @blur="save(0)"
            @focus="focused = true"
            @keydown.enter="save(0)"
            @update:model-value="onInputStart"
          >
            <template #prefix>
              <VIcon class="v-input__icon" icon="Calendar" type="placeholder" />
            </template>

            <template #below>
              <VErrors :errors />
            </template>
          </VInput>

          <span class="v-datepicker__input-separator" :class="{ 'has-label': hasLabel }">-</span>

          <VInput
            ref="inputEnd"
            :class="{ focused, 'has-label': hasLabel }"
            :clearable
            :disabled
            :error="errors.length ? ' ' : ''"
            :help
            :label="endLabel"
            :maxlength="maxLength"
            :minimal
            :model-value="currentValue[1]"
            :optional
            :placeholder="endPlaceholder"
            :required="isRequired"
            :size
            :status
            :suggestion="currentValue[1] ? undefined : suggestion"
            @blur="save(1)"
            @clear="clear"
            @focus="focused = true"
            @keydown.enter="save(1)"
            @update:model-value="onInputEnd"
          />
        </div>
      </template>

      <VCalendar
        :clear-button-text
        :clearable
        :confirm-button-text
        :date-is-disabled
        is-range
        :max="toDayjs(max)"
        :min="toDayjs(min)"
        :model-value="[currentValue[0] ? toDayjs(currentValue[0]) : null, currentValue[1] ? toDayjs(currentValue[1]) : null]"
        :precision
        :quick-options
        :style="{ width: `${popperWidth}px` }"
        @confirm="onConfirm"
        @update:model-value="currentValue = fromValue($event)"
      />
    </VPopper>
  </div>
</template>

<script setup lang="ts" generic="TModel extends DateValue | DateValue[]">
import { get, isEqual } from 'lodash-es'
import { computed, inject, nextTick, onMounted, ref, useTemplateRef, watchEffect } from 'vue'

import { useErrors } from '../composables/useErrors.ts'
import { useResizeObserver } from '../composables/useResizeObserver.ts'
import type { VCalendarPrecision, VCalendarQuickOption, VInputSize, VInputStatus } from '../types.ts'
import { configVDatePicker as config } from '../utils/config.ts'
import { type Dayjs, dayjs, isDayjs } from '../utils/day.ts'
import { nextFrame } from '../utils/helpers.ts'
import { $formKey } from '../utils/injections.ts'
import VCalendar from './Calendar/VCalendar.vue'
import VIcon from './Icon/VIcon.vue'
import VErrors from './VErrors.vue'
import VInput from './VInput.vue'
import VPopper from './VPopper.vue'

export type DateValue = string | (Date | null) | (Dayjs | null)

interface Props {
  quickOptions?: VCalendarQuickOption[]
  isRange?: boolean
  valueFormat?: string
  displayFormat?: string
  clearable?: boolean
  disabled?: boolean | null
  status?: VInputStatus
  error?: string | string[]
  help?: string | string[]
  required?: boolean | null
  optional?: boolean
  minimal?: boolean
  focusOnMount?: boolean
  placeholder?: string
  label?: string
  startPlaceholder?: string
  startLabel?: string
  endPlaceholder?: string
  endLabel?: string
  min?: any
  max?: any
  // min?: DateValue
  // max?: DateValue
  prop?: string
  dateIsDisabled?: (date: Dayjs) => boolean
  clearButtonText?: string
  confirmButtonText?: string
  precision?: VCalendarPrecision
  size?: VInputSize
}

const props = withDefaults(defineProps<Props>(), {
  quickOptions: undefined,
  isRange: false,
  valueFormat: 'YYYY-MM-DD',
  displayFormat: undefined,
  clearable: false,
  disabled: null,
  status: '',
  error: '',
  help: '',
  required: null,
  optional: false,
  minimal: false,
  focusOnMount: false,
  placeholder: '',
  label: '',
  startPlaceholder: '',
  startLabel: '',
  endPlaceholder: '',
  endLabel: '',
  min: null,
  max: null,
  prop: '',
  dateIsDisabled: () => false,
  clearButtonText: undefined,
  confirmButtonText: undefined,
  precision: 'days',
  size: undefined,
})

const model = defineModel<TModel>({ default: null })

const form = inject($formKey, undefined)

const errors = useErrors(props)

const focused = ref(false)
const currentValue = ref<string | [string, string]>('')
const popperWidth = ref(0)
const popper = useTemplateRef('popper')
const input = useTemplateRef('input')
const inputEnd = useTemplateRef('inputEnd')
const inputs = useTemplateRef('inputs')

const _displayFormat = computed(() => props.displayFormat ?? config.displayFormat)
const hasLabel = computed(() => props.startLabel.length > 0 || props.endLabel.length > 0)

const maxLength = computed((): undefined | number => {
  // We only set a max length for certain formats.
  // Some formats have variable length, so we don't want to limit them.
  switch (_displayFormat.value) {
    case 'YYYY-MM-DD':
      return 10
    case 'DD-MMM-YYYY':
      return 11
    case 'DD/MM/YYYY':
      return 10
    case 'MM/DD/YYYY':
      return 10
    default:
      return undefined
  }
})

const unknownFormats = ['Z', 'A', 'a', 'h']
const suggestion = computed((): undefined | string => {
  if (unknownFormats.some((format) => _displayFormat.value.includes(format))) {
    return undefined
  }

  if (_displayFormat.value.includes('D') && !_displayFormat.value.includes('DD')) {
    return undefined
  }

  if (_displayFormat.value.includes('M') && !_displayFormat.value.includes('MM')) {
    return undefined
  }

  if (_displayFormat.value.includes('MMMM')) {
    return undefined
  }

  return _displayFormat.value
})

const isRequired = computed(() => {
  if (props.required) {
    return true
  }

  if (props.required === null && props.prop) {
    const rules = get(form?.props?.rules, props.prop, {})

    return 'required' in rules && rules.required
  }

  return false
})

watchEffect(() => (currentValue.value = fromValue(model.value)))

onMounted(async () => {
  useResizeObserver(inputs, onReferenceResize)

  if (props.focusOnMount) {
    await nextFrame()
    popper.value?.show()
  }
})

function fromValue(value: DateValue): string
function fromValue(value: DateValue[]): [string, string]
function fromValue(value: DateValue | DateValue[]): string | [string, string]
function fromValue(value: DateValue | DateValue[]): string | [string, string] {
  if (Array.isArray(value)) {
    if (value.length !== 2) {
      return ['', '']
    }

    return [fromValue(value[0]), fromValue(value[1])]
  }

  if (isDayjs(value)) {
    return value.format(_displayFormat.value)
  }

  if (value instanceof Date) {
    return dayjs(value).format(_displayFormat.value)
  }

  if (!value) {
    return ''
  }

  return dayjs(value, props.valueFormat).format(_displayFormat.value)
}

function toValue(displayValue: string | null): DateValue {
  if (!displayValue) {
    return null
  }

  const date = toDayjs(displayValue)

  if (date === null) {
    return null
  }

  if (model.value instanceof Date) {
    return date.toDate()
  }

  if (isDayjs(model.value)) {
    return date
  }

  return date.format(props.valueFormat)
}

function toDayjs(value: DateValue) {
  if (value === null || isDayjs(value)) {
    return value
  }

  if (value instanceof Date) {
    return dayjs(value)
  }

  return dayjs(value, _displayFormat.value)
}

function clear() {
  let value = null
  if (Array.isArray(model.value) || props.isRange) {
    value = []
  } else if (typeof model.value === 'string') {
    value = ''
  }

  model.value = value as TModel
}

function onInputStart(value: string) {
  currentValue.value = [autoSeparator(value), currentValue.value[1]]
}

function onInputEnd(value: string) {
  currentValue.value = [currentValue.value[0], autoSeparator(value)]
}

async function save(index: number | null = null) {
  const previousValue = model.value
  focused.value = false

  let invalid = false
  if (Array.isArray(currentValue.value)) {
    if (!validate(currentValue.value[0]) || (index === 0 && toDayjs(currentValue.value[1])?.isBefore(toDayjs(currentValue.value[0])))) {
      invalid = true
      currentValue.value[0] = fromValue(Array.isArray(model.value) ? model.value[0] : null)
    }
    if (!validate(currentValue.value[1]) || (index === 1 && toDayjs(currentValue.value[1])?.isBefore(toDayjs(currentValue.value[0])))) {
      invalid = true
      currentValue.value[1] = fromValue(Array.isArray(model.value) ? model.value[1] : null)
    }

    if (invalid) return
  } else if (!validate(currentValue.value)) {
    currentValue.value = fromValue(model.value)

    return
  }

  if (Array.isArray(model.value)) {
    let value = [toValue(currentValue.value[0]), toValue(currentValue.value[1])]
    value = value[0] && value[1] ? value : []

    if (value.length === 0 && !props.clearable) {
      // Non-clearable datepicker should not allow you to clear the inputs.
      currentValue.value = fromValue(model.value)
    } else if (!isEqual(value, model.value)) {
      model.value = value as TModel
    }
  } else {
    const value = toValue(Array.isArray(currentValue.value) ? null : currentValue.value)
    if (!value && !props.clearable) {
      currentValue.value = fromValue(model.value)
    } else {
      model.value = value as TModel
    }
  }

  await nextTick()
  if (props.prop && !isEqual(previousValue, model.value) && (previousValue || model.value)) {
    input.value?.form?.validateOneProp(props.prop)
  }
}

function validate(value: DateValue): boolean {
  if (!value) {
    return true
  }

  if (Array.isArray(value)) {
    return validate(value[0]) && validate(value[1])
  }
  let date = value

  if (!isDayjs(date)) {
    // Require strict parsing
    date = dayjs(value, _displayFormat.value, true)
  }

  if (props.min && date.isBefore(props.min)) return false
  if (props.max && date.isAfter(props.max)) return false
  if (props.dateIsDisabled(date)) return false

  return date.isValid()
}

function onInput(input: string, event?: InputEvent) {
  currentValue.value = input

  if (event?.inputType && !event.inputType.includes('delete')) {
    currentValue.value = autoSeparator(currentValue.value)
  }
}

function autoSeparator(value: string) {
  if (['-', '/'].includes(_displayFormat.value[value.length])) {
    return `${value}${_displayFormat.value[value.length]}`
  }

  return value
}

function onReferenceResize({ width }: DOMRectReadOnly) {
  popperWidth.value = width
}

function hidePopper() {
  popper.value?.hide()
}

function onConfirm() {
  hidePopper()
  if (props.isRange) {
    inputEnd.value?.focus()
  } else {
    input.value?.focus()
  }
}

defineExpose({
  clear,
  currentValue,
  fromValue,
  popper,
  save,
  toDayjs,
  toValue,
})
</script>

<style>
.v-datepicker {
  width: 100%;
}

.v-datepicker__popper {
  background: #fff;
  filter: var(--drop-shadow-light);
  border-radius: 3px;
}

.v-datepicker__inputs {
  position: relative;
  display: flex;
  align-items: center;
}

.v-datepicker__inputs.is-range .v-input.has-input .v-input__inner {
  text-align: center;
}

.v-datepicker__inputs.is-range .v-input:first-child .v-input__inner {
  border-right: none;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.v-datepicker__inputs.is-range .v-input .v-input__inner {
  border-color: var(--border-color);
}

.v-datepicker__inputs.is-range.is-hovering .v-input .v-input__inner {
  border-color: var(--color-text-placeholder);
}

.v-datepicker__inputs.is-range.is-focused .v-input .v-input__inner {
  border-color: var(--color-primary);
}

.v-datepicker__inputs.is-range .v-input:last-child .v-input__inner {
  border-left: none;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.v-datepicker__input-separator {
  padding: 0px 5px;
  position: absolute;
  left: 50%;
  z-index: 1;
  transform: translateX(-50%);
  color: var(--color-info);
}

.v-datepicker__input-separator.has-label {
  padding-top: 21px;
  padding-bottom: 0px;
}
</style>
